import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Upload, X, Save, Image } from 'lucide-react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { ImageActions } from '@xeger/quill-image-actions';
import QuillAutoDetectUrl from 'quill-auto-detect-url';
import api from '../api/axiosInstance';
import { PermissionManager } from '../utils/PermissionManager';
import { BoardFormData, BoardFile } from './types';
import './css/board.css';

// Quill에 모듈 등록
Quill.register('modules/imageActions', ImageActions);
Quill.register('modules/autoDetectUrl', QuillAutoDetectUrl);

interface ExistingFile {
  id: number;
  originalName: string;
  fileSize: number;
  fileType?: string;
  downloadUrl?: string;
}

const BoardWrite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const isEditMode = !!editId;

  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // Quill 에디터 ref
  const quillRef = useRef<ReactQuill>(null);

  // 이미지 업로드 공통 함수 (툴바 버튼, Ctrl+V 모두 이 함수 사용)
  const uploadAndInsertImage = useCallback(async (file: File) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('이미지 파일만 업로드할 수 있습니다. (JPG, PNG, GIF, WebP)');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('이미지 파일 크기는 5MB 이하로 업로드해주세요.');
      return;
    }

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await api.post('/api/board/upload-image', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = response.data.url;
      const imageId = response.data.id;
      const imageName = response.data.originalName;

      setUploadedImages(prev => [...prev, { url: imageUrl, name: imageName, id: imageId }]);

      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection(true);

        const img = new window.Image();
        img.onload = () => {
          quill.insertEmbed(range.index, 'image', imageUrl);

          requestAnimationFrame(() => {
            const images = quill.root.querySelectorAll('img');
            const lastImage = images[images.length - 1] as HTMLImageElement;
            if (lastImage && lastImage.src === imageUrl) {
              const editorWidth = quill.root.clientWidth - 20;
              const fitWidth = Math.min(img.naturalWidth, editorWidth);
              lastImage.setAttribute('width', String(Math.round(fitWidth)));
            }
          });

          quill.setSelection(range.index + 1);
        };
        img.src = imageUrl;
      }
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      alert('이미지 업로드에 실패했습니다.');
    }
  }, []);

  // Quill 이미지 핸들러 - 툴바 버튼용
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      uploadAndInsertImage(file);
    };
  }, [uploadAndInsertImage]);

  // Quill 에디터 설정
  const quillModules = useMemo(() => ({
    imageActions: {},
    autoDetectUrl: true,
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }), [imageHandler]);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list',
    'align',
    'link', 'image',
    'width', 'height'
  ];

  const [formData, setFormData] = useState<BoardFormData>({
    title: '',
    content: '',
    isNotice: false // DB에는 없지만 DTO 호환성 유지
  });

  const [files, setFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
  const [existingImages, setExistingImages] = useState<ExistingFile[]>([]);
  const [filesToDelete, setFilesToDelete] = useState<number[]>([]);

  // 이미지 삽입 관련 상태
  const [uploadedImages, setUploadedImages] = useState<{url: string, name: string, id: number}[]>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [errors, setErrors] = useState({
    title: '',
    content: ''
  });

  // Ctrl+V 이미지 붙여넣기 처리 - 툴바 이미지 버튼과 동일한 경로로 서버 업로드
  // 부모 컨테이너에서 capture phase로 이벤트를 가로채서 Quill clipboard보다 먼저 처리
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const editorContainer = quill.root.parentNode as HTMLElement;
    if (!editorContainer) return;

    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.stopPropagation(); // Quill clipboard에 이벤트 전달 차단
          e.preventDefault();
          const file = item.getAsFile();
          if (file) uploadAndInsertImage(file);
          return;
        }
      }
    };

    editorContainer.addEventListener('paste', handlePaste, true); // capture phase
    return () => editorContainer.removeEventListener('paste', handlePaste, true);
  }, [uploadAndInsertImage]);

  // 초기 인증 체크
  useEffect(() => {
    const initAuth = async () => {
      try {
        const userNameRes = await api.get('/api/UserName');
        const userInfo = userNameRes.data;
        console.log('User info from API:', userInfo);

        if (!userInfo.authenticated) {
          alert('로그인이 필요합니다.');
          navigate('/');
          return;
        }

        const userId = userInfo.id || '';
        console.log('Setting currentUserId:', userId);
        setCurrentUserId(String(userId));

        // 게시글 작성 권한 체크
        if (!PermissionManager.canCreateBoardPost()) {
          alert('게시글 작성 권한이 없습니다.');
          navigate('/board');
          return;
        }

        setAuthChecked(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        alert('인증에 실패했습니다. 다시 로그인해주세요.');
        navigate('/');
      }
    };

    initAuth();
  }, []);

  // 수정 모드일 때 게시글 불러오기
  useEffect(() => {
    const loadPost = async () => {
      if (!isEditMode) {
        return;
      }

      if (!authChecked || currentUserId === '') {
        console.log('인증 대기 중', { authChecked, currentUserId });
        return;
      }

      try {
        console.log('Loading post for edit:', editId);
        const response = await api.get(`/api/board/${editId}`);
        const post = response.data;
        console.log('Loaded post data:', post);

        // 폼 데이터 설정
        setFormData({
          id: post.id,
          title: post.title,
          content: post.content,
          isNotice: post.isNotice || false
        });

        if (post.files) {
          // 이미지와 일반 파일 분리
          const images = post.files.filter((file: ExistingFile) => {
            const fileType = file.fileType?.toLowerCase() || '';
            const fileName = file.originalName?.toLowerCase() || '';
            return fileType.startsWith('image/') ||
                   fileName.endsWith('.jpg') ||
                   fileName.endsWith('.jpeg') ||
                   fileName.endsWith('.png') ||
                   fileName.endsWith('.gif') ||
                   fileName.endsWith('.webp');
          });
          const otherFiles = post.files.filter((file: ExistingFile) => {
            const fileType = file.fileType?.toLowerCase() || '';
            const fileName = file.originalName?.toLowerCase() || '';
            return !(fileType.startsWith('image/') ||
                    fileName.endsWith('.jpg') ||
                    fileName.endsWith('.jpeg') ||
                    fileName.endsWith('.png') ||
                    fileName.endsWith('.gif') ||
                    fileName.endsWith('.webp'));
          });
          setExistingImages(images);
          setExistingFiles(otherFiles);
        }

        // 권한 체크
        if (!PermissionManager.canModifyAllBoardPosts() && post.authorId !== currentUserId) {
          alert('수정 권한이 없습니다.');
          navigate('/board');
        }
      } catch (error) {
        console.error('Failed to fetch post:', error);
        alert('게시글을 불러오는데 실패했습니다.');
        navigate('/board');
      }
    };

    loadPost();
  }, [editId, authChecked, currentUserId, isEditMode, navigate]);

  const validateForm = () => {
    const newErrors = {
      title: '',
      content: ''
    };

    if (!formData.title.trim()) {
      newErrors.title = '제목을 입력해주세요.';
    } else if (formData.title.length > 100) {
      newErrors.title = '제목은 100자 이내로 입력해주세요.';
    }

    if (!formData.content.trim()) {
      newErrors.content = '내용을 입력해주세요.';
    }

    setErrors(newErrors);
    return !newErrors.title && !newErrors.content;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      const totalFiles = files.length + existingFiles.length + existingImages.length - filesToDelete.length + selectedFiles.length;

      if (totalFiles > 5) {
        alert('최대 5개의 파일만 첨부할 수 있습니다.');
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      for (const file of selectedFiles) {
        if (file.size > maxSize) {
          alert(`파일 크기는 10MB 이하로 업로드해주세요. (${file.name})`);
          return;
        }
      }

      setFiles(prev => [...prev, ...selectedFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingFile = (fileId: number) => {
    console.log('Removing file:', fileId);
    setFilesToDelete(prev => {
      const updated = [...prev, fileId];
      console.log('Files marked for deletion:', updated);
      return updated;
    });
  };

  const restoreExistingFile = (fileId: number) => {
    console.log('Restoring file:', fileId);
    setFilesToDelete(prev => {
      const updated = prev.filter(id => id !== fileId);
      console.log('Files marked for deletion after restore:', updated);
      return updated;
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();

      // 게시글 데이터를 JSON으로 추가
      const postData: BoardFormData = {
        title: formData.title,
        content: formData.content,
        isNotice: formData.isNotice,
        imageIds: uploadedImages.map(img => img.id)
      };

      if (isEditMode && formData.id) {
        postData.id = formData.id;
        // 삭제할 파일 ID들 추가
        if (filesToDelete.length > 0) {
          postData.filesToDelete = filesToDelete;
          console.log('Files to delete in submission:', filesToDelete);
        }
      }

      console.log('Submitting post data:', postData);

      submitData.append('post', new Blob([JSON.stringify(postData)], { type: 'application/json' }));

      // 파일들 추가
      files.forEach(file => {
        submitData.append('files', file);
      });

      let response;
      if (isEditMode) {
        response = await api.put(`/api/board/${editId}`, submitData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        response = await api.post('/api/board', submitData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      alert(isEditMode ? '게시글이 수정되었습니다.' : '게시글이 작성되었습니다.');
      navigate(`/board/${response.data.id}`);
    } catch (error) {
      console.error('Submit failed:', error);
      alert(isEditMode ? '게시글 수정에 실패했습니다.' : '게시글 작성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="board-container">
      <header className="board-header">
        <h1 className="board-title">
          {isEditMode ? '게시글 수정' : '게시글 작성'}
        </h1>
        <div className="header-right">
          <button
            className="back-btn"
            onClick={() => navigate('/board')}
          >
            <ArrowLeft size={20} />
            목록으로
          </button>
        </div>
      </header>

      <div className="board-content">
        <form onSubmit={handleSubmit} className="write-form">
          <div className="form-group">
            <label htmlFor="title" className="form-label">
              제목 <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="제목을 입력하세요"
              className={`form-input ${errors.title ? 'error' : ''}`}
              maxLength={100}
            />
            {errors.title && <span className="error-text">{errors.title}</span>}
          </div>


          <div className="form-group">
            <label htmlFor="content" className="form-label">
              내용 <span className="required">*</span>
            </label>

            <div className={`quill-editor-container ${errors.content ? 'error' : ''}`} spellCheck={false}>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={formData.content}
                onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                modules={quillModules}
                formats={quillFormats}
                placeholder="내용을 입력하세요"
              />
            </div>
            {errors.content && <span className="error-text">{errors.content}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">파일 첨부</label>
            <div className="file-upload-section">
              <input
                type="file"
                id="file-input"
                onChange={handleFileChange}
                multiple
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt"
                className="file-input-hidden"
              />
              <label htmlFor="file-input" className="file-upload-btn">
                <Upload size={18} />
                파일 선택
              </label>
              <span className="file-info">
                최대 5개, 각 파일당 10MB 이하
              </span>
            </div>

            {/* 기존 이미지 목록 (수정 모드) */}
            {isEditMode && existingImages.length > 0 && (
              <div className="existing-images">
                <h4>기존 첨부 이미지</h4>
                <div className="image-preview-grid">
                  {existingImages.map(file => (
                    <div
                      key={file.id}
                      className={`image-preview-item ${filesToDelete.includes(file.id) ? 'deleted' : ''}`}
                    >
                      <img
                        src={file.downloadUrl || `${process.env.REACT_APP_API_HOST}/api/board/image/${file.id}`}
                        alt={file.originalName}
                        className={`preview-image ${filesToDelete.includes(file.id) ? 'faded' : ''}`}
                      />
                      <span className="image-name">{file.originalName}</span>
                      <span className="file-size">({formatFileSize(file.fileSize)})</span>
                      {filesToDelete.includes(file.id) ? (
                        <button
                          type="button"
                          onClick={() => restoreExistingFile(file.id)}
                          className="restore-btn"
                        >
                          복원
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => removeExistingFile(file.id)}
                          className="remove-btn"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 기존 파일 목록 (수정 모드) */}
            {isEditMode && existingFiles.length > 0 && (
              <div className="existing-files">
                <h4>기존 첨부파일</h4>
                {existingFiles.map(file => (
                  <div
                    key={file.id}
                    className={`file-item ${filesToDelete.includes(file.id) ? 'deleted' : ''}`}
                  >
                    <span className="file-name">{file.originalName}</span>
                    <span className="file-size">({formatFileSize(file.fileSize)})</span>
                    {filesToDelete.includes(file.id) ? (
                      <button
                        type="button"
                        onClick={() => restoreExistingFile(file.id)}
                        className="restore-btn"
                      >
                        복원
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeExistingFile(file.id)}
                        className="remove-btn"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* 새로 선택한 파일 목록 */}
            {files.length > 0 && (
              <div className="new-files">
                <h4>새 첨부파일</h4>
                {files.map((file, index) => (
                  <div key={index} className="file-item">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">({formatFileSize(file.size)})</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="remove-btn"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={loading}
              className="submit-btn"
            >
              <Save size={18} />
              {loading ? '저장 중...' : isEditMode ? '수정' : '작성'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/board')}
              className="cancel-btn"
            >
              취소
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BoardWrite;
